import { useEffect, useState } from "react";
import { IInventoryItemDetails } from "../../../../../@types/InventoryItems";

import { ConvertStringWithoutValueToDefault } from "../../../../../shared/ConvertStringWithoutValueToDefault";
import * as S from "./styles";
import { Input } from "../../../../../components/New/Input";
import { useTranslation } from "react-i18next";
import { formatProductPrice } from "../../../../../shared/Formatter";
import { useAuth } from "../../../../../hooks/auth";

interface ItemDetailsCardProps {
  title: string;
  search?: boolean;
  data: IInventoryItemDetails;
}

export function ItemDetailsCard({
  title,
  data,
  search = false,
}: ItemDetailsCardProps) {
  const { t } = useTranslation();
  const [currency, setCurrency] = useState<string>();
  const { fetchTenantData } = useAuth();

  const getCurrencyFromTenant = async (tenantName: string) => {
    const tenantData = await fetchTenantData(tenantName);

    if (tenantData) {
      setCurrency(tenantData.currency);
    }
  };

  useEffect(() => {
    if (data?.lightspeedProduct?.tenantName) {
      getCurrencyFromTenant(data.lightspeedProduct.tenantName);
    }
  }, [data?.lightspeedProduct?.tenantName]);

  const [stockFilter, setStoreFilter] = useState("");
  const [storesWithStock, setStoresWithStock] = useState(false);

  const stockValue = data?.stock?.filter(
    ({ name }) => name === "In Stock",
  )[0] ?? {
    name: undefined,
    quantity: undefined,
  };

  const filteredStock = data.stock
    ? data.stock
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((stock) => {
          const partialStores = stock.name
            .toLocaleLowerCase()
            .includes(stockFilter.toLocaleLowerCase());

          if (storesWithStock) {
            return (
              partialStores && stock.quantity > 0 && stock.name !== "In Stock"
            );
          }

          return partialStores && stock.name !== "In Stock";
        })
    : [];

  return (
    <S.ItemDetailsCardContainer>
      <S.ItemDetailsCardTitle>{title}</S.ItemDetailsCardTitle>
      {search && (
        <S.SearchInputContainer>
          <Input
            disabled={!search}
            placeholder={t("search")}
            onChange={(event) => setStoreFilter(event.target.value)}
          />
        </S.SearchInputContainer>
      )}
      {title === t("stock") && (
        <S.FilterContainer>
          <S.FilterButton onClick={() => setStoresWithStock(!storesWithStock)}>
            {t("storesWithStockOnly")}
          </S.FilterButton>
          <input
            type="checkbox"
            checked={storesWithStock}
            onChange={() => setStoresWithStock(!storesWithStock)}
            onClick={() => setStoresWithStock(!storesWithStock)}
          />
        </S.FilterContainer>
      )}

      <S.ItemDetailsContent search={search}>
        {title === t("identification") && (
          <>
            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("upc")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer>
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(data.upc)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("ean")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer>
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(data.ean)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("customSku")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer>
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(data.customSku)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>
                {t("manufacturerSku")}
              </S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer>
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(data.manufacturerSku)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>
          </>
        )}

        {title === t("pricing") && (
          <>
            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("default")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={
                  title === t("pricing") || title === t("location")
                }
              >
                <S.ItemDetailsValue>
                  {formatProductPrice(data?.prices?.default, currency)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("msrp")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={
                  title === t("pricing") || title === t("location")
                }
              >
                <S.ItemDetailsValue>
                  {formatProductPrice(data?.prices?.msrp, currency)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("online")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={
                  title === t("pricing") || title === t("location")
                }
              >
                <S.ItemDetailsValue>
                  {formatProductPrice(data?.prices?.online, currency)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("ca")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={
                  title === t("pricing") || title === t("location")
                }
              >
                <S.ItemDetailsValue>
                  {formatProductPrice(data?.prices?.ca, currency)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("ts")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={
                  title === t("pricing") || title === t("location")
                }
              >
                <S.ItemDetailsValue>
                  {formatProductPrice(data?.prices?.ts, currency)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("defaultCost")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={
                  title === t("pricing") || title === t("location")
                }
              >
                <S.ItemDetailsValue>
                  {formatProductPrice(data?.prices?.defaultCost, currency)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>
          </>
        )}

        {title === t("stock") && (
          <S.StockContainer>
            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>
                {ConvertStringWithoutValueToDefault(stockValue.name)}
              </S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isSpecial={title === t("stock") || title === t("location")}
              >
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(stockValue.quantity)}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>
            {filteredStock.map(({ name, quantity }, index) => (
              <S.ItemDetailsRow key={index}>
                <S.ItemDetailsProperty>{name}</S.ItemDetailsProperty>
                <S.ItemDetailsValueContainer
                  isSpecial={title === t("stock") || title === t("location")}
                >
                  <S.ItemDetailsValue>
                    {ConvertStringWithoutValueToDefault(quantity)}
                  </S.ItemDetailsValue>
                </S.ItemDetailsValueContainer>
              </S.ItemDetailsRow>
            ))}
          </S.StockContainer>
        )}

        {title === t("location") && (
          <>
            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("boxNumber")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={title === t("location")}
                hasLongText={
                  (data.location?.boxNumber?.location?.length ?? 0) > 9
                }
              >
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(
                    data.location?.boxNumber?.location,
                  )}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("backstock1")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={title === t("location")}
                hasLongText={
                  (data.location?.boxNumber?.location?.length ?? 0) > 9
                }
              >
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(
                    data.location?.backStock1?.location,
                  )}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("backstock2")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={title === t("location")}
                hasLongText={
                  (data.location?.boxNumber?.location?.length ?? 0) > 9
                }
              >
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(
                    data.location?.backStock2?.location,
                  )}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>

            <S.ItemDetailsRow>
              <S.ItemDetailsProperty>{t("backstock3")}</S.ItemDetailsProperty>
              <S.ItemDetailsValueContainer
                isPricingOrLocation={title === t("location")}
                hasLongText={
                  (data.location?.boxNumber?.location?.length ?? 0) > 9
                }
              >
                <S.ItemDetailsValue>
                  {ConvertStringWithoutValueToDefault(
                    data.location?.backStock3?.location,
                  )}
                </S.ItemDetailsValue>
              </S.ItemDetailsValueContainer>
            </S.ItemDetailsRow>
          </>
        )}
      </S.ItemDetailsContent>
    </S.ItemDetailsCardContainer>
  );
}
